import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import styles from "./CenterDetail.module.sass";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";
import NameAndDescription from "./NameAndDescription";
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import { getCenterById, countActiveCenterContracts, subscribeToCenterContracts, unsubscribeFromContracts } from "../../services/center.service";

const navigation = ["Coordonnées"];

const CenterDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(0);
  const [center, setCenter] = useState(location.state?.center || null);
  const [loading, setLoading] = useState(!location.state?.center);
  const [error, setError] = useState(null);
  const [activeEnrollmentsCount, setActiveEnrollmentsCount] = useState(0);

  const fetchCenter = useCallback(async () => {
    if (location.state?.center) return;

    setLoading(true);
    setError(null);
    try {
      const centerData = await getCenterById(id);
      if (centerData) {
        setCenter(centerData);
      } else {
        setError("Centre non trouvé");
        setTimeout(() => navigate("/admin/centers/"), 2000);
      }
    } catch (err) {
      console.error("Error fetching center:", err);
      setError("Impossible de charger les détails du centre");
      setTimeout(() => navigate("/admin/centers/"), 2000);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, location.state]);

  useEffect(() => {
    if (id) {
      fetchCenter();
    } else {
      setError("ID de centre non valide");
      setTimeout(() => navigate("/admin/centers/"), 2000);
    }
  }, [id, navigate, fetchCenter]);

  useEffect(() => {
    if (!center || !center.id) return;

    const fetchActiveEnrollmentsCount = async () => {
      try {
        const count = await countActiveCenterContracts(center.id);
        setActiveEnrollmentsCount(count);
      } catch (error) {
        console.error('Error counting active enrollments:', error);
      }
    };

    fetchActiveEnrollmentsCount();

    // S'abonner aux changements de contrats pour ce centre
    const subscriptionId = subscribeToCenterContracts(center.id, (e) => {
      console.log('Événement de contrat détecté:', e);
      // Mettre à jour le compteur quand un contrat est créé, mis à jour ou supprimé
      fetchActiveEnrollmentsCount();
    });

    // Se désabonner quand le composant est démonté ou quand le centre change
    return () => {
      unsubscribeFromContracts(subscriptionId);
    };
  }, [center]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loading}>Chargement des détails du centre...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>{error}</div>
        <div>Redirection vers la liste des centres...</div>
      </div>
    );
  }

  if (!center) {
    return null;
  }

  return (
    <>
      <div className={styles.shop}>
        <Link className={cn("button-white", styles.button)} to="/admin/centers">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>
          <div className={styles.profile}>
            <div className={styles.details}>
              <div className={styles.avatar}>
                <img src={center.imageUrl || '/images/content/school_logo.png'} alt="Center Logo" />
              </div>
              <div className={styles.wrap}>
                <div className={cn("h4", styles.man)}>{center.name || 'Centre'}</div>
                <div className={styles.login}>
                  {center.is_company_cfa ? (
                    <span className={cn('status-type', 'status-blue')}>CFA d'entreprise</span>
                  ) : (
                    <span className={cn('status-type', 'status-green')}>Centre de Formation</span>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.statusContainer}>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Inscriptions actives</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  (activeEnrollmentsCount > 0) ? 'status-green-dark' : 'status-gray-dark'
                )}>
                  {activeEnrollmentsCount} inscription(s)
                </div>
              </div>
            </div>
          </div>

          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>

            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex(x => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>
        </Card>

        <div className={styles.wrap}>
          {activeIndex === 0 && (
            <>
              <NameAndDescription center={center} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CenterDetail;